/**
 * Etiquetas amigables (en español) para los estados de la solicitud. El backend
 * persiste el estado como nombre de enum (p. ej. `EnAutorizacionNivel1`); acá se
 * traduce a algo legible para mostrar en badges/tooltips.
 *
 * Los estados "en autorización" además dependen del nivel/rol actual (primer
 * nivel, segundo nivel, Mesa de Control), por eso `etiquetaEstado` acepta el
 * contexto del flujo · ver `EnAutorizacion`.
 */
const ESTADO_LABEL: Record<string, string> = {
  Iniciada: "Iniciada",
  TipoSeleccionado: "Tipo seleccionado",
  SubtipoSeleccionado: "Subtipo seleccionado",
  // Tipos CA/OC · paso previo a cargar el CFDI (asociar contrato / orden).
  ContratoAnualizadoSeleccionado: "Contrato asociado",
  OrdenDeCompraSeleccionada: "Orden asociada",
  CfdiPendiente: "CFDI pendiente",
  CfdiCargado: "CFDI cargado",
  EnValidacionReglas: "Validando reglas",
  ReglasRechazadas: "Reglas rechazadas",
  EnValidacionFiscal: "Validando en el SAT",
  Validado: "Validado",
  RechazadoFiscal: "Rechazado por el SAT",
  SuspendidaFiscal: "Suspendida (SAT)",
  CapturandoDatos: "Capturando datos",
  SeleccionandoProveedor: "Seleccionando proveedor",
  Prorrateando: "Prorrateando",
  ProvisionalmenteListo: "Provisionalmente listo",
  AdjuntandoDocumentos: "Adjuntando documentos",
  ListoParaAutorizacion: "Listo para autorización",
  // Autorización · fallback estático (sin contexto de nivel). etiquetaEstado los
  // reemplaza por la versión con nivel/rol cuando recibe el flujo.
  EnAutorizacionNivel1: "En autorización primer nivel",
  EnAutorizacionNivelN: "En autorización",
  RechazadaParaCorreccion: "Rechazada para corrección",
  Autorizada: "Autorizada",
  EnColaContabilizacion: "En cola de contabilización",
  PolizaGenerada: "Póliza generada",
  EnviadaAContabilizacion: "Enviada a contabilización",
  DadaDeBajaPorAutorizador: "Baja por Mesa de Control",
  CanceladaPorSolicitante: "Cancelada",
  DescartadaPorPlazoVencido: "Descartada (plazo vencido)",
};

const ORDINALES = ["", "primer", "segundo", "tercer", "cuarto", "quinto", "sexto"];

function ordinal(n: number): string {
  return ORDINALES[n] ?? `nivel ${n}`;
}

/** Contexto del flujo para etiquetar los estados "en autorización". */
export interface ContextoFlujo {
  nivelActual: number | null;
  /** Roles autorizadores del nivel actual (CSV) · para detectar Mesa de Control. */
  rolActual: string | null;
}

/** Estados en los que la solicitud está en algún nivel del flujo de autorización. */
export const ESTADOS_EN_AUTORIZACION = new Set([
  "EnAutorizacionNivel1",
  "EnAutorizacionNivelN",
]);

/**
 * Estados "en proceso de captura" · la solicitud aún la está armando el
 * solicitante (pre-envío a autorización). Excluye los estados rechazados
 * (contados en "Requieren revisión") y los terminales/de autorización.
 */
export const ESTADOS_EN_CAPTURA = new Set([
  "Iniciada",
  "TipoSeleccionado",
  "SubtipoSeleccionado",
  "ContratoAnualizadoSeleccionado",
  "OrdenDeCompraSeleccionada",
  "CfdiPendiente",
  "CfdiCargado",
  "EnValidacionReglas",
  "EnValidacionFiscal",
  "Validado",
  "CapturandoDatos",
  "SeleccionandoProveedor",
  "Prorrateando",
  "ProvisionalmenteListo",
  "AdjuntandoDocumentos",
  "ListoParaAutorizacion",
]);

/**
 * Etiqueta amigable de un estado. Para los estados "en autorización" usa el
 * contexto del flujo: "En autorización Mesa de Control" si el nivel actual lo
 * autoriza Mesa de Control, o "En autorización {primer|segundo|…} nivel".
 */
export function etiquetaEstado(estado: string, flujo?: ContextoFlujo): string {
  if (ESTADOS_EN_AUTORIZACION.has(estado) && flujo) {
    if (flujo.rolActual && flujo.rolActual.includes("MesaDeControl")) {
      return "En autorización Mesa de Control";
    }
    if (flujo.nivelActual && flujo.nivelActual > 0) {
      return `En autorización ${ordinal(flujo.nivelActual)} nivel`;
    }
  }
  return ESTADO_LABEL[estado] ?? estado;
}
